import { View } from 'react-native'
import cn from '@ui/utils/cn'
import Typography from '@ui/typography'
import { TypographyProps } from '@ui/typography/types'

import { backgroundColorViewField, sViewFieldContainer, WIDTH } from './styles'
import FormInput, { FormInputProps } from '@ui/forms/Input/FormInput'
import { FieldValues } from 'react-hook-form'
import FormSelect, { FormSelectProps } from '@ui/forms/Select/FormSelect'
import { refineCommaInput, RefractionValidations } from '@ca/common'
import { getPartialValidate } from './getPartialValidate'
import { useMemo } from 'react'
import { SelectOption } from '@ui/forms/Select/SelectProps'

export const Label = ({
  width = WIDTH,
  children,
  className,
  ...props
}: TypographyProps & { width?: number }) => (
  <Typography
    variant="MDB"
    className={cn('color-text/25 text-center', className)}
    style={{ width, minWidth: 24 }}
    {...props}
  >
    {children}
  </Typography>
)

function isRefFromKey(key: string): key is keyof typeof RefractionValidations {
  return key in RefractionValidations
}

export const getSchema = (key: string) =>
  isRefFromKey(key) ? RefractionValidations[key] : undefined

export const InputField = <T extends FieldValues>(props: FormInputProps<T>) => {
  const [_, key] = props.name.split('_')
  const schema = getSchema(key)

  return (
    <FormInput
      inputMode="numeric"
      center
      hideIndicator
      refine={refineCommaInput}
      validate={getPartialValidate(schema)}
      style={{ width: WIDTH }}
      {...props}
    />
  )
}

export const RefractionField = <T extends FieldValues>(
  props: Omit<FormSelectProps<T, string>, 'options'> & {
    keyname?: string
  }
) => {
  const key = props.keyname || props.name.split('_')[1]
  const schema = getSchema(key)

  const options: SelectOption<string>[] = useMemo(() => {
    const options = []
    if (!schema) return []

    const min: number = schema.min || 0
    const max: number = schema.max || 0
    const step: number = schema.step || 1
    const precision = step === 1 ? 0 : 2

    for (
      let num = max;
      num >= min;
      num = parseFloat((num - step).toFixed(2)) // NOTE: floating point precision fix
    ) {
      const value = num.toFixed(precision)
      options.push({ value, label: value })
    }
    return options
  }, [schema])

  return (
    <FormSelect
      center
      hideIndicator
      hiddenChevron
      style={{ width: WIDTH }}
      classNameTextInput="px-0 text-center w-full"
      classNamePopover="w-max"
      classNameOption="p-0"
      mark="underline"
      options={options}
      {...props}
      clearValue={props.clearValue}
    />
  )
}

export const SelectField = <T extends FieldValues>(
  props: FormSelectProps<T>
) => {
  return (
    <FormSelect
      center
      hiddenChevron
      style={{ width: WIDTH }}
      classNameTextInput="px-0 text-center w-full"
      classNamePopover="w-max"
      classNameOption="p-0"
      mark="underline"
      {...props}
    />
  )
}

export interface ViewFieldProps {
  children: React.ReactNode
  className?: string
  classNameText?: string
  fullWidth?: boolean
  fixed?: boolean
  error?: boolean
  disabled?: boolean
  plus?: boolean
}

export const ViewField = ({
  children,
  className,
  classNameText,
  fullWidth,
  plus,
  fixed = true,
  error = false,
  disabled = false,
}: ViewFieldProps) => {
  const value =
    fixed && typeof children === 'number' ? children.toFixed(2) : children
  return (
    <View
      style={{
        width: fullWidth ? '100%' : WIDTH,
        backgroundColor: backgroundColorViewField,
      }}
      className={cn(
        sViewFieldContainer,
        'h-12',
        error && 'border-red-500 bg-red-50',
        disabled && 'border-black/0 bg-black/5',
        className
      )}
    >
      <Typography
        variant="MDB"
        className={cn(
          classNameText,
          'uppercase',
          // 'font-text-semibold',
          disabled && 'color-black/25',
          error && 'color-red-500'
        )}
      >
        {plus && Number(value) > 0 ? '+' : ''}
        {value}
      </Typography>
    </View>
  )
}
